// Article tarifaire du parcours Scan-To-Charge
const CHARGE_NOW_ARTICLE = "CHARGE-NOW";

// Prix affiché quand l'API ne renvoie aucun tarif CHARGE-NOW exploitable.
// Choix produit assumé : on affiche ce tarif de repli plutôt que de bloquer
// le parcours, y compris sur l'écran de paiement.
export const DEFAULT_PRICE = "0.49";

// tarifs CHARGE-NOW portés par un connecteur
const findConnectorRates = (connector) => {
    if (!connector || !Array.isArray(connector.ratesByArticle)) {
        return [];
    }

    return connector.ratesByArticle
        .filter(article => article && article.article === CHARGE_NOW_ARTICLE && Array.isArray(article.rates))
        .flatMap(article => article.rates)
        .filter(Boolean);
};

// comparer deux numéros de connecteur par valeur : l'API peut sérialiser
// connector_number en chaîne
const isSameConnector = (a, b) => {
    if (a == null || b == null) {
        return false;
    }

    const left = Number(a);
    const right = Number(b);

    return Number.isFinite(left) && Number.isFinite(right) && left === right;
};

// tarifs CHARGE-NOW au niveau borne, aplatis depuis le groupement par article.
// L'API renvoie une entrée par connecteur, discriminée par connector_number.
const findStationRates = (evcpData, connectorNumber) => {
    if (!evcpData || !Array.isArray(evcpData.rates)) {
        return [];
    }

    const chargeNowRates = evcpData.rates
        .filter(entry => entry && entry.article === CHARGE_NOW_ARTICLE && Array.isArray(entry.rates))
        .flatMap(entry => entry.rates)
        .filter(Boolean);

    const connectorRates = chargeNowRates.filter(
        rate => isSameConnector(rate.connector_number, connectorNumber)
    );
    if (connectorRates.length > 0) {
        return connectorRates;
    }

    // Bornes à tarif unique : l'API ne renvoie pas connector_number
    return chargeNowRates.filter(rate => rate.connector_number == null);
};

// formater le tarif brut de l'API ("0.3500", 0.35, "0.5") en prix
// affichable au centime ("0.35", "0.50")
const formatRate = (rate) => {
    if (!rate) {
        return DEFAULT_PRICE;
    }

    const raw = typeof rate.rate === 'string' ? rate.rate.replace(',', '.') : rate.rate;
    const value = Number.parseFloat(raw);

    if (!Number.isFinite(value) || value < 0) {
        return DEFAULT_PRICE;
    }

    return value.toFixed(2);
};

// determiner le tarif actuel en fonction de l'heure
export const getCurrentRate = (rates) => {
    if (!rates || rates.length === 0) return null;

    // S'il n'y a qu'un seul tarif, le retourner
    if (rates.length === 1) {
        return rates[0];
    }

    const now = new Date();
    const currentHour = now.getHours();
    const currentMinute = now.getMinutes();
    const currentTimeInMinutes = currentHour * 60 + currentMinute;

    // Parcourir les tarifs pour trouver celui qui correspond à l'heure actuelle
    for (const rate of rates) {
        if (!rate.beginTime) continue;

        // Extraire les heures et minutes du début
        const beginTimeParts = rate.beginTime.split('h');
        const beginHour = parseInt(beginTimeParts[0]);
        const beginMinute = parseInt(beginTimeParts[1] || 0);
        const beginTimeInMinutes = beginHour * 60 + beginMinute;

        // Si endTime est null, cela signifie que ce tarif s'applique 24h/24
        if (!rate.endTime) {
            return rate;
        }

        // Extraire les heures et minutes de la fin
        const endTimeParts = rate.endTime.split('h');
        const endHour = parseInt(endTimeParts[0]);
        const endMinute = parseInt(endTimeParts[1] || 0);
        let endTimeInMinutes = endHour * 60 + endMinute;

        // Gérer le cas où l'heure de fin est le lendemain (ex: 22h00 à 06h00)
        if (endTimeInMinutes < beginTimeInMinutes) {
            // Si l'heure actuelle est après l'heure de début OU avant l'heure de fin
            if (currentTimeInMinutes >= beginTimeInMinutes || currentTimeInMinutes < endTimeInMinutes) {
                return rate;
            }
        } else {
            // Cas normal (même jour)
            if (currentTimeInMinutes >= beginTimeInMinutes && currentTimeInMinutes < endTimeInMinutes) {
                return rate;
            }
        }
    }

    // Si aucun tarif ne correspond, retourner le premier par défaut
    return rates[0];
};

// Prix CHARGE-NOW d'un connecteur donné. Les connecteurs d'une même borne
// peuvent avoir des tarifs différents [QCT-389].
export const getConnectorPrice = (connector, evcpData) => {
    // 1: tarifs portés par le connecteur lui-même
    const connectorRates = findConnectorRates(connector);
    if (connectorRates.length > 0) {
        return formatRate(getCurrentRate(connectorRates));
    }

    // 2: fallback sur les tarifs borne, filtrés sur ce connecteur
    const stationRates = findStationRates(evcpData, connector?.number);
    if (stationRates.length > 0) {
        return formatRate(getCurrentRate(stationRates));
    }

    return DEFAULT_PRICE;
};

// convertir un nombre to lettre
export const numberToLetter = (number) => {
    if (typeof number !== 'number' || number <= 0) return '';
    return String.fromCharCode(96 + number).toUpperCase();
};
